import React, { useMemo } from 'react';
import { Box, Button, ListSubheader, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { useOperateWorkspace, type MissionChromeExtraAction } from './OperateWorkspaceContext';
import { extractAlertFromIncident } from '../../../lib/operationalRemediation';
import {
  buildMissionSelectorItems,
  missionBarMeta,
  type MissionSelectorItem,
} from '../../../lib/missionSelectorItems';
import { useEngineeringRequests } from '../../../services/engineeringRequests';
import { useChangeRequests } from '../../../services/api';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import type { EngineeringContextRef } from '../workspace/projectPhases';
import { BORDER, INK, TYPE } from '../../../design-system/foundation';
import { StatusBadge } from '../../../design-system/v2';
import { BarChart3 } from 'lucide-react';
import '../workspace/mission-header-aurora.css';

const SELECT_VALUE_SEP = '::';

function metaPillTone(part: string): 'danger' | 'warn' | 'info' | 'neutral' {
  const m = /^(\w+) severity$/i.exec(part);
  if (!m) return 'neutral';
  const level = m[1].toLowerCase();
  if (level === 'critical' || level === 'high') return 'danger';
  if (level === 'medium') return 'warn';
  return 'info';
}

/**
 * `info` actions move the mission forward — the first one is the primary
 * (contained) action. `running` marks a human override (e.g. waive tests)
 * and `critical` a destructive one; both stay secondary.
 */
function isPrimaryTone(tone?: MissionChromeExtraAction['tone']): boolean {
  return tone === undefined || tone === 'info';
}

function secondaryColor(tone?: MissionChromeExtraAction['tone']): 'inherit' | 'error' | 'warning' {
  if (tone === 'critical') return 'error';
  if (tone === 'running') return 'warning';
  return 'inherit';
}

const SECTION_LABEL: Record<MissionSelectorItem['section'], string> = {
  requests: 'Requests',
  insights: 'Insights',
  incidents: 'Incidents',
};

/**
 * Unified Mission selector — Requests, Insights-originated missions, and
 * Incidents only. Deduped; resolved missions labeled; sections labeled.
 */
/** When the mission card is shown, Refresh / Rerun / last-updated live in its menu instead. */
const EngineeringContextPicker: React.FC<{ chromeInCard?: boolean }> = ({ chromeInCard = false }) => {
  const { projectId = '' } = useParams();
  const {
    incidents,
    incidentsLoading,
    incident,
    engineeringContext: contextFromWorkspace,
    goToContext,
    missionChromeActions,
  } = useOperateWorkspace();
  const [searchParams] = useSearchParams();
  const engineeringContext: EngineeringContextRef =
    contextFromWorkspace ??
    (() => {
      const inc = searchParams.get('incident');
      if (inc) return { kind: 'incident', id: inc };
      const req = searchParams.get('request');
      if (req) return { kind: 'request', id: req };
      return null;
    })();
  const requestsQ = useEngineeringRequests(projectId);
  const crsQ = useChangeRequests(projectId);

  const items = useMemo(
    () =>
      buildMissionSelectorItems({
        requests: requestsQ.data ?? [],
        incidents,
        changeRequests: crsQ.data?.change_requests ?? [],
      }),
    [requestsQ.data, incidents, crsQ.data?.change_requests],
  );

  const bySection = useMemo(() => {
    const groups: Record<MissionSelectorItem['section'], MissionSelectorItem[]> = {
      requests: [],
      insights: [],
      incidents: [],
    };
    for (const item of items) groups[item.section].push(item);
    return groups;
  }, [items]);

  const selectValue = engineeringContext
    ? `${engineeringContext.kind}${SELECT_VALUE_SEP}${engineeringContext.id}`
    : '';

  const handleChange = (val: string) => {
    if (!val) {
      goToContext(null);
      return;
    }
    // Limit 2: ids must never be truncated if they ever contain the separator.
    const [kind, id] = val.split(SELECT_VALUE_SEP, 2);
    if (!kind || !id) return;
    goToContext(kind === 'request' ? { kind: 'request', id } : { kind: 'incident', id });
  };

  const loading = incidentsLoading || requestsQ.isLoading || crsQ.isLoading;
  const showChrome =
    Boolean(missionChromeActions) &&
    Boolean(
      (!chromeInCard && (missionChromeActions?.onRerun || missionChromeActions?.onRefresh)) ||
        (missionChromeActions?.extraActions && missionChromeActions.extraActions.length > 0),
    );

  const selectedItem = items.find(
    (i) =>
      engineeringContext &&
      i.kind === engineeringContext.kind &&
      i.id === engineeringContext.id,
  );

  const { pathname } = useLocation();
  const metaLine = /\/operate\/(test-environment|executions)/.test(pathname)
    ? ''
    : engineeringContext?.kind === 'incident' && incident
      ? missionBarMeta({
          applicationKey: incident.application_key || undefined,
          severity: incident.severity,
          resolved: selectedItem?.resolved,
        })
      : engineeringContext?.kind === 'request' && selectedItem
        ? missionBarMeta({
            recognition: selectedItem.section === 'insights' ? 'From Insights' : 'Request',
            resolved: selectedItem.resolved,
            statusLabel: selectedItem.statusLabel,
          })
        : '';

  const extras = missionChromeActions?.extraActions || [];
  const primaryExtra = extras.find((a) => isPrimaryTone(a.tone)) ?? null;
  const secondaryExtras = extras.filter((a) => a !== primaryExtra);

  const renderExtraAction = (action: MissionChromeExtraAction, primary: boolean) => (
    <Tooltip key={action.id} title={action.tooltip || ''}>
      <span>
        <Button
          size="small"
          variant={primary ? 'contained' : 'outlined'}
          color={primary ? 'primary' : secondaryColor(action.tone)}
          onClick={action.onClick}
          disabled={action.disabled || action.busy || missionChromeActions?.busy}
        >
          {action.busy ? action.busyLabel || 'Working…' : action.label}
        </Button>
      </span>
    </Tooltip>
  );

  // MUI Select clones only *direct* children for onClick/onChange. Fragments
  // (and Dividers wrapping sections) swallow selection — menu opens, click does nothing.
  const selectChildren = useMemo(() => {
    const sectionOrder: MissionSelectorItem['section'][] = ['requests', 'insights', 'incidents'];
    const nodes: React.ReactNode[] = [
      <MenuItem key="__empty" value="">
        <Box component="span" sx={{ color: 'text.secondary' }}>
          Select a mission
        </Box>
      </MenuItem>,
    ];
    for (const section of sectionOrder) {
      const list = bySection[section];
      if (!list.length) continue;
      nodes.push(
        <ListSubheader
          key={`hdr-${section}`}
          disableSticky
          sx={{ ...TYPE.overline, color: INK.tertiary, lineHeight: '32px', bgcolor: 'background.paper' }}
        >
          {SECTION_LABEL[section]}
        </ListSubheader>,
      );
      for (const item of list) {
        const value = `${item.kind}${SELECT_VALUE_SEP}${item.id}`;
        nodes.push(
          <MenuItem
            key={value}
            value={value}
            sx={{
              opacity: item.resolved ? 0.72 : 1,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 420,
              }}
            >
              {item.title}
            </Typography>
            {item.resolved ? <StatusBadge tone="success" label="Resolved" sx={{ flexShrink: 0 }} /> : null}
          </MenuItem>,
        );
      }
    }
    return nodes;
  }, [bySection]);

  return (
    <Box
      className="ops-scope"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: '10px 16px',
        mb: 1,
        pb: 2,
        borderBottom: `1px solid ${BORDER.subtle}`,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px', minWidth: 280, flex: '0 1 640px' }}>
        <span className="ops-eyebrow accent">Mission</span>
        <Select
          className="ops-dd"          value={selectValue}
          onChange={(e) => handleChange(e.target.value as string)}
          disabled={loading}
          displayEmpty
          fullWidth
          size="small"
          variant="outlined"
          inputProps={{ 'aria-label': 'Mission' }}
          renderValue={(val) => {
            const label = ((): React.ReactNode => {
            if (!val) return <Box component="span" sx={{ color: 'text.secondary' }}>{loading ? 'Loading missions…' : 'Select a mission'}</Box>;
            const [kind, id] = String(val).split(SELECT_VALUE_SEP, 2);
            const item = items.find((i) => i.kind === kind && i.id === id);
            if (!item) {
              if (kind === 'incident' && incident) {
                return extractAlertFromIncident(incident)?.signal || incident.title;
              }
              return 'Selected mission';
            }
            return item.resolved ? `${item.title} (Resolved)` : item.title;
            })();
            return (
              <>
                <i className="ops-dot" aria-hidden />
                <span className="ops-txt">{label}</span>
              </>
            );
          }}
        >
          {selectChildren}
        </Select>
      </Box>

      {metaLine ? (
        <div className="ops-meta" aria-label="Mission status">
          {metaLine.split(' · ').map((part, i) => {
            const tone = metaPillTone(part);
            return (
              <span key={`${i}-${part}`} className={`ops-pill ${tone}`}>
                {tone !== 'neutral' ? (
                  <span className="ops-ico">
                    <BarChart3 size={11} strokeWidth={2.4} />
                  </span>
                ) : null}
                {part}
              </span>
            );
          })}
        </div>
      ) : null}


      {showChrome && missionChromeActions ? (
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            maxWidth: '100%',
          }}
        >
          {/* Order: secondary actions first, the one primary action last (GUIDELINES §6). */}
          {secondaryExtras.map((action) => renderExtraAction(action, false))}
          {primaryExtra ? renderExtraAction(primaryExtra, true) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default EngineeringContextPicker;
